import {
  calculateAgeFromBirthDate,
  FundingPolicyType,
  jointApplicationRoutingInstructions,
  jointApplicationSourceTrackingInstructions,
  primaryInsuredPlaceholder,
  secondaryInsuredPlaceholder,
  systemPrefillPrompt,
  userPrefillPrompt,
} from '@shared';
import {
  AdvisorInfoWithWritingCode,
  getAdvisorUpdatesForNationwide,
  getAdvisorUpdatesForNationwideUnderwriting,
  getClientUpdatesForNationwide,
  getJointOwnerUpdatesForNationwide,
  getPolicyUpdatesForNationwide,
  NATIONWIDE_PREFILL_SECTIONS_PRIMARY,
  NATIONWIDE_PREFILL_SECTIONS_SECONDARY,
  NATIONWIDE_UNDERWRITING_PREFILL_SECTIONS,
  PARTNER_PREFIX,
} from '.';

import {
  getAdvisorUpdatesForOneAmerica,
  getClientUpdatesForOneAmerica,
  getPolicyUpdatesForOneAmerica,
  getStaticUpdatesForOneAmerica,
  ONE_AMERICA_PREFILL_SECTIONS_PRIMARY,
  ONE_AMERICA_PREFILL_SECTIONS_SECONDARY,
  ONEAMERICA_UNDERWRITING_PREFILL_SECTIONS,
} from './one-america-util';
import {
  CARE_SCOUT_PREFILL_SECTIONS_PRIMARY,
  getAdvisorUpdatesForCareScout,
  getClientUpdatesForCarescout,
  getPolicyUpdatesForCareScout,
} from './care-scout-util';
import {
  SECURIAN_PREFILL_SECTIONS_PRIMARY,
  getAdvisorUpdatesForSecurian,
  getClientUpdatesForSecurian,
  getPolicyUpdatesForSecurian,
} from './securian-util';
import {
  getAdvisorUpdatesForLincoln,
  getClientUpdatesForLincoln,
  getPolicyUpdatesForLincoln,
  LINCOLN_PREFILL_SECTIONS_PRIMARY,
} from './lincoln-util';

const sectionMap: Record<string, Record<string, string[]>> = {
  'underwriting-prefill': {
    oneamerica: ONEAMERICA_UNDERWRITING_PREFILL_SECTIONS,
    nationwide: NATIONWIDE_UNDERWRITING_PREFILL_SECTIONS,
  },
  'application-prefill': {
    oneamerica: ONE_AMERICA_PREFILL_SECTIONS_PRIMARY,
    nationwide: NATIONWIDE_PREFILL_SECTIONS_PRIMARY,
    carescout: CARE_SCOUT_PREFILL_SECTIONS_PRIMARY,
    securian: SECURIAN_PREFILL_SECTIONS_PRIMARY,
    lincoln: LINCOLN_PREFILL_SECTIONS_PRIMARY,
  },
  'joint-application-prefill': {
    oneamerica: ONE_AMERICA_PREFILL_SECTIONS_SECONDARY,
    nationwide: NATIONWIDE_PREFILL_SECTIONS_SECONDARY,
  },
};

export const enrichPrefillData = (
  prefillData: PrefillData[],
  clientInfo: Client,
  advisorInfo: AdvisorInfoWithWritingCode,
  policyFundingSource: PolicyFundingSource,
  formConfig?: PolicyFormConfig,
): PrefillData[] => {
  let enhancedData = [...prefillData];

  const nationwideFormTransformer = () => {
    if (advisorInfo) {
      const advisorUpdates = getAdvisorUpdatesForNationwide(advisorInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, advisorUpdates);
    }
    if (clientInfo) {
      const clientUpdates = getClientUpdatesForNationwide(clientInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, clientUpdates);
    }
    if (policyFundingSource) {
      const fundingPolicyUpdates = getPolicyUpdatesForNationwide(policyFundingSource);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, fundingPolicyUpdates);
    }
    // Map joint owner data from pre-application form to main application form
    const jointOwnerUpdates = getJointOwnerUpdatesForNationwide(enhancedData);
    enhancedData = updateMultiplePrefillAnswers(enhancedData, jointOwnerUpdates);
  };

  const oneAmericaFormTransformer = () => {
    const staticUpdates = getStaticUpdatesForOneAmerica();
    enhancedData = updateMultiplePrefillAnswers(enhancedData, staticUpdates);
    if (advisorInfo) {
      const advisorUpdates = getAdvisorUpdatesForOneAmerica(advisorInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, advisorUpdates);
    }
    if (clientInfo) {
      const clientUpdates = getClientUpdatesForOneAmerica(clientInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, clientUpdates);
    }
    if (policyFundingSource) {
      const fundingPolicyUpdates = getPolicyUpdatesForOneAmerica(policyFundingSource);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, fundingPolicyUpdates);
    }
  };

  const careScoutFormTransformer = () => {
    if (clientInfo) {
      const clientUpdates = getClientUpdatesForCarescout(clientInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, clientUpdates);
    }
    if (advisorInfo) {
      const advisorUpdates = getAdvisorUpdatesForCareScout(advisorInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, advisorUpdates);
    }
    if (policyFundingSource) {
      const fundingPolicyUpdates = getPolicyUpdatesForCareScout(policyFundingSource);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, fundingPolicyUpdates);
    }
  };

  const securianFormTransformer = () => {
    if (clientInfo) {
      const clientUpdates = getClientUpdatesForSecurian(clientInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, clientUpdates);
    }
    if (advisorInfo) {
      const advisorUpdates = getAdvisorUpdatesForSecurian(advisorInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, advisorUpdates);
    }
    if (policyFundingSource) {
      const fundingPolicyUpdates = getPolicyUpdatesForSecurian(policyFundingSource, clientInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, fundingPolicyUpdates);
    }
  };

  const lincolnFormTransformer = () => {
    if (clientInfo) {
      const clientUpdates = getClientUpdatesForLincoln(clientInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, clientUpdates);
    }
    if (advisorInfo) {
      const advisorUpdates = getAdvisorUpdatesForLincoln(advisorInfo);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, advisorUpdates);
    }
    if (policyFundingSource) {
      const policyUpdates = getPolicyUpdatesForLincoln(policyFundingSource);
      enhancedData = updateMultiplePrefillAnswers(enhancedData, policyUpdates);
    }
  };

  // Determine transformer based on carrier and formType from formConfig
  let transformer: (() => void) | undefined;

  if (formConfig?.formMetadata) {
    const carrier = formConfig.formMetadata.carrier.toLowerCase();
    const formType = formConfig.formMetadata.formType;
    // Special handling for Nationwide underwriting form (pre-underwriting-form)
    if (carrier === 'nationwide' && formType === 'pre-underwriting-form') {
      transformer = () => {
        if (advisorInfo) {
          const advisorUpdates = getAdvisorUpdatesForNationwideUnderwriting(advisorInfo);
          enhancedData = updateMultiplePrefillAnswers(enhancedData, advisorUpdates);
        }
        if (clientInfo?.intakeSurvey?.clientBirthDate) {
          const age = calculateAgeFromBirthDate(clientInfo.intakeSurvey.clientBirthDate);
          if (age !== null && age > 0) {
            enhancedData = updatePrefillAnswer(
              enhancedData,
              'age',
              age.toString(),
              ['clientBirthDate'],
              'number',
            );
          }
        }
      };
    }
    // Standard carrier transformers for application forms
    else if (carrier === 'nationwide') {
      transformer = nationwideFormTransformer;
    } else if (carrier === 'oneamerica') {
      transformer = oneAmericaFormTransformer;
    } else if (
      carrier === 'carescout assurance' ||
      carrier === 'carescout' ||
      carrier === 'care assurance'
    ) {
      transformer = careScoutFormTransformer;
    } else if (carrier === 'securian' || carrier === 'securian financial') {
      transformer = securianFormTransformer;
    } else if (carrier === 'lincoln' || carrier === 'lincoln financial') {
      transformer = lincolnFormTransformer;
    }
  }

  // Execute form-specific transformer if it exists
  if (transformer) {
    transformer();
  }
  return enhancedData;
};

export const getPolicyUpdatesForCarrier = (
  carrier: string,
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  const carrierLower = carrier.toLowerCase();

  if (carrierLower === 'oneamerica') {
    return getPolicyUpdatesForOneAmerica(policyFundingSource);
  } else if (carrierLower === 'nationwide') {
    return getPolicyUpdatesForNationwide(policyFundingSource);
  } else if (
    carrierLower === 'carescout assurance' ||
    carrierLower === 'carescout' ||
    carrierLower === 'care assurance'
  ) {
    return getPolicyUpdatesForCareScout(policyFundingSource);
  } else if (carrierLower === 'securian' || carrierLower === 'securian financial') {
    return getPolicyUpdatesForSecurian(policyFundingSource);
  } else if (carrierLower === 'lincoln' || carrierLower === 'lincoln financial') {
    return getPolicyUpdatesForLincoln(policyFundingSource);
  }

  return [];
};

export const getStaticPrefillUpdatesForCarrier = (
  carrier: string,
  advisorInfo: AdvisorInfoWithWritingCode,
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  const carrierLower = carrier.toLowerCase();
  let updates: PreFillUpdate[] = [];

  if (carrierLower === 'oneamerica') {
    // OneAmerica static updates
    const staticUpdates = getStaticUpdatesForOneAmerica();
    const advisorUpdates = getAdvisorUpdatesForOneAmerica(advisorInfo);
    const policyUpdates = getPolicyUpdatesForOneAmerica(policyFundingSource);

    updates = [...staticUpdates, ...advisorUpdates, ...policyUpdates];
  } else if (carrierLower === 'nationwide') {
    // Nationwide static updates
    const advisorUpdates = getAdvisorUpdatesForNationwide(advisorInfo);
    const policyUpdates = getPolicyUpdatesForNationwide(policyFundingSource);

    updates = [...advisorUpdates, ...policyUpdates];

    // Note: getJointOwnerUpdatesForNationwide requires existing prefillData
    // It will need to be handled separately if needed
  } else if (
    carrierLower === 'carescout assurance' ||
    carrierLower === 'carescout' ||
    carrierLower === 'care assurance'
  ) {
    // CareScout static updates
    const advisorUpdates = getAdvisorUpdatesForCareScout(advisorInfo);
    const policyUpdates = getPolicyUpdatesForCareScout(policyFundingSource);

    updates = [...advisorUpdates, ...policyUpdates];
  } else if (carrierLower === 'securian' || carrierLower === 'securian financial') {
    // Securian static updates
    const advisorUpdates = getAdvisorUpdatesForSecurian(advisorInfo);
    const policyUpdates = getPolicyUpdatesForSecurian(policyFundingSource);

    updates = [...advisorUpdates, ...policyUpdates];
  } else if (carrierLower === 'lincoln' || carrierLower === 'lincoln financial') {
    // Lincoln static updates
    const advisorUpdates = getAdvisorUpdatesForLincoln(advisorInfo);
    const policyUpdates = getPolicyUpdatesForLincoln(policyFundingSource);

    updates = [...advisorUpdates, ...policyUpdates];
  }

  return updates;
};

const updateMultiplePrefillAnswers = (
  prefillData: PrefillData[],
  updates: Array<{
    questionRef: string;
    answer: PolicyFormSubmissionDataValue;
    type?: string;
    sourceQuestionRefs?: string[];
  }>,
): PrefillData[] => {
  let updatedData = [...prefillData];

  updates.forEach(({ questionRef, answer, type = 'text', sourceQuestionRefs }) => {
    updatedData = updatePrefillAnswer(updatedData, questionRef, answer, sourceQuestionRefs, type);
  });

  return updatedData;
};

const updatePrefillAnswer = (
  prefillData: PrefillData[],
  questionRef: string,
  answer: PolicyFormSubmissionDataValue,
  sourceQuestionRefs?: string[],
  type: string = 'text',
): PrefillData[] => {
  const existingIndex = prefillData.findIndex(item => item.questionRef === questionRef);

  if (existingIndex !== -1) {
    const updatedData = [...prefillData];
    updatedData[existingIndex] = {
      ...updatedData[existingIndex],
      answer,
      sourceQuestionRefs,
    };
    return updatedData;
  } else {
    return [
      ...prefillData,
      {
        questionRef,
        type,
        answer,
        sourceQuestionRefs,
      },
    ];
  }
};

export const getRelevantCitations = (
  allCitations: PrefillCitations[],
  prefillData: PrefillData[],
  allPartnerCitations?: PrefillCitations[],
): PrefillCitations[] => {
  // Fast lookups
  const citationMap = new Map<string, PrefillCitations>();
  allCitations.forEach(c => citationMap.set(c.ref, c));

  const partnerMap = new Map<string, PrefillCitations>();
  allPartnerCitations?.forEach(c => partnerMap.set(c.ref, c));

  const relevantByRef = new Map<string, PrefillCitations>();

  // Helper function to replace field references in titles
  const replaceFieldReferences = (
    title: string,
    sourceMap: Map<string, PrefillCitations>,
  ): string => {
    const fieldPattern = /\{\{field:([a-zA-Z0-9_-]+)\}\}/g;
    return title.replace(fieldPattern, (match, questionRef) => {
      // Find citation with matching ref in the provided map
      const citation = sourceMap.get(questionRef);
      if (citation && citation.answer !== undefined && citation.answer !== null) {
        return String(citation.answer);
      }
      return match; // Keep original if not found
    });
  };

  for (const item of prefillData) {
    if (item.sourceQuestionRefs) {
      for (const ref of item.sourceQuestionRefs) {
        if (allPartnerCitations && ref.startsWith(PARTNER_PREFIX)) {
          const rawRef = ref.slice(PARTNER_PREFIX.length);
          const partnerHit = partnerMap.get(rawRef);
          if (partnerHit) {
            const adjusted: PrefillCitations = {
              ...partnerHit,
              ref,
              title: replaceFieldReferences(partnerHit.title, partnerMap),
            };
            relevantByRef.set(adjusted.ref, adjusted);
          }
        } else {
          const hit = citationMap.get(ref);
          if (hit) {
            const adjustedHit: PrefillCitations = {
              ...hit,
              title: replaceFieldReferences(hit.title, citationMap),
            };
            relevantByRef.set(adjustedHit.ref, adjustedHit);
          }
        }
      }
    }
  }
  return Array.from(relevantByRef.values());
};

//Utilities for backend

export const getPrefillQuestionsForPolicy = (
  formDef: PolicyFormConfig,
  fundingPolicyType: FundingPolicyType,
  promptType: string,
): Partial<PolicyFormQuestion>[] => {
  const policySection = getPolicySections(fundingPolicyType, promptType);
  const { sections, questions } = formDef.formDefinition;
  const filteredSections = sections.filter((section: PolicyFormSection) =>
    policySection.includes(section.id),
  );
  const questionRefs: string[] = [];
  filteredSections.forEach((section: PolicyFormSection) => {
    if (section.subsections) {
      section.subsections.forEach((subsection: PolicyFormSubSection) => {
        if (subsection.questionGroups) {
          subsection.questionGroups.forEach((group: PolicyFormQuestionGroup) => {
            if (group.questions) {
              questionRefs.push(...group.questions);
            }
          });
        }
      });
    }
  });
  const selectedQuestions = questionRefs
    .filter(ref => questions[ref])
    .map(ref => {
      const question = questions[ref];
      return {
        questionRef: question.questionRef,
        title: question.title,
        type: question.type,
        options: question.options,
        columns: question.columns,
        characterLimit: question.characterLimit,
      };
    });
  return selectedQuestions;
};

export const getPromptForPrefill = (
  promptType: string,
  typeformPayload: string,
  formDefinitions: string,
  primaryClientData?: string,
): { systemPrompt: string; userPrompt: string } => {
  return promptType === 'joint-application-prefill'
    ? getJointApplicationPrompt(typeformPayload, formDefinitions, primaryClientData!)
    : getApplicationPrompt(typeformPayload, formDefinitions);
};

const getPolicySections = (fundingPolicyType: FundingPolicyType, promptType: string): string[] => {
  const policyCarrier = getCarrierFromPolicyType(fundingPolicyType);
  const sections = sectionMap[promptType]?.[policyCarrier];
  return sections || [];
};

const getApplicationPrompt = (
  typeformPayload: string,
  formDefinitions: string,
): { systemPrompt: string; userPrompt: string } => {
  const systemPrompt = systemPrefillPrompt
    .replace('{{APPLICANT_ROUTING_PLACEHOLDER}}', '')
    .replace('{{PARTNER_TRACKING_PLACEHOLDER}}', '');

  const userPrompt = userPrefillPrompt
    .replace('typeform_payload_placeholder', typeformPayload)
    .replace('form_definition_placeholder', formDefinitions);

  return { systemPrompt, userPrompt };
};

const getJointApplicationPrompt = (
  typeformPayload: string,
  formDefinitions: string,
  primaryClientData: string,
): { systemPrompt: string; userPrompt: string } => {
  const primaryInsuredIntake = primaryInsuredPlaceholder.replace(
    'typeform_payload_placeholder_primary',
    primaryClientData,
  );

  const secondaryInsuredIntake = secondaryInsuredPlaceholder.replace(
    'typeform_payload_placeholder_secondary',
    typeformPayload,
  );

  const jointIntakeData = primaryInsuredIntake + '\n\n' + secondaryInsuredIntake;

  const systemPrompt = systemPrefillPrompt
    .replace('{{APPLICANT_ROUTING_PLACEHOLDER}}', jointApplicationRoutingInstructions)
    .replace('{{PARTNER_TRACKING_PLACEHOLDER}}', jointApplicationSourceTrackingInstructions);

  const userPrompt = userPrefillPrompt
    .replace('typeform_payload_placeholder', jointIntakeData)
    .replace('form_definition_placeholder', formDefinitions);
  return { systemPrompt, userPrompt };
};

const getCarrierFromPolicyType = (fundingPolicyType: FundingPolicyType): string => {
  if (
    fundingPolicyType === FundingPolicyType.assetCare ||
    fundingPolicyType === FundingPolicyType.jointAssetCare
  ) {
    return 'oneamerica';
  }
  if (fundingPolicyType === FundingPolicyType.careScoutCareAssurance) {
    return 'carescout';
  }
  if (
    fundingPolicyType === FundingPolicyType.careMattersTwo ||
    fundingPolicyType === FundingPolicyType.jointCareMatters
  ) {
    return 'nationwide';
  }
  if (
    fundingPolicyType === FundingPolicyType.secureCareThree ||
    fundingPolicyType === FundingPolicyType.secureCareFour
  ) {
    return 'securian';
  }
  if (fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    return 'lincoln';
  }
  return '';
};

export const filterUncitedPrefills = (
  enrichedPrefills: PrefillData[],
  citations: PrefillCitations[],
): PrefillData[] => {
  const citationRefs = new Set(citations.map(c => c.ref));
  const filteredPrefills = enrichedPrefills.filter(prefill => {
    if (!prefill.sourceQuestionRefs || prefill.sourceQuestionRefs.length === 0) {
      return true;
    }
    return prefill.sourceQuestionRefs.some(ref => citationRefs.has(ref));
  });
  return filteredPrefills ?? [];
};
